import React from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { ShieldCheck, FileBadge2, Lock, KeyRound } from "lucide-react";

const GROUP_META = {
  compliance: { icon: FileBadge2, accent: "#2563EB" },
  security: { icon: ShieldCheck, accent: "#10B981" },
  auth: { icon: KeyRound, accent: "#111111" },
};

const Certifications = () => {
  const { t } = useI18n();
  const groups = [
    { key: "compliance", title: t.certifications.groupCompliance },
    { key: "security", title: t.certifications.groupSecurity },
    { key: "auth", title: t.certifications.groupAuth },
  ];

  return (
    <section id="certifications" className="py-20 md:py-32 bg-white" data-testid="certifications-section">
      <div className="zdc-container">
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F5F7FA] border border-[#E5E7EB] mb-4">
            <ShieldCheck className="w-3.5 h-3.5 text-[#10B981]" />
            <span className="text-xs font-medium text-[#111111]">{t.certifications.eyebrow}</span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#111111]">
            {t.certifications.title}
          </h2>
          <p className="mt-4 text-base md:text-lg text-[#6B7280]">{t.certifications.subtitle}</p>
        </div>

        <div className="space-y-10 md:space-y-14">
          {groups.map((g) => {
            const items = t.certifications.items.filter((i) => i.group === g.key);
            const meta = GROUP_META[g.key];
            const GroupIcon = meta.icon;
            return (
              <div key={g.key}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${meta.accent}10`, color: meta.accent }}>
                    <GroupIcon className="w-4 h-4" strokeWidth={1.75} />
                  </div>
                  <h3 className="font-display text-lg md:text-xl font-semibold text-[#111111]">{g.title}</h3>
                  <div className="flex-1 h-px bg-[#E5E7EB]" />
                  <span className="text-xs text-[#6B7280]">{items.length}</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {items.map((it, i) => (
                    <div
                      key={i}
                      data-testid="certification-card"
                      className="group relative p-5 rounded-2xl border border-[#E5E7EB] bg-white card-hover hover:border-[#111111]/30 hover:shadow-[0_20px_40px_rgba(17,17,17,0.06)]"
                    >
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div
                          className="px-2 py-0.5 rounded-md text-[10px] font-bold tracking-tight"
                          style={{ background: `${meta.accent}10`, color: meta.accent }}
                        >
                          {it.code}
                        </div>
                        <Lock className="w-3.5 h-3.5 text-[#6B7280] opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <div className="font-display text-sm font-semibold text-[#111111] mb-1.5 leading-tight">
                        {it.name}
                      </div>
                      <p className="text-xs text-[#6B7280] leading-relaxed">{it.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Trust strip */}
        <div className="mt-14 md:mt-20 p-6 md:p-8 rounded-2xl bg-[#F5F7FA] border border-[#E5E7EB] flex flex-col md:flex-row items-start md:items-center gap-5 md:gap-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#2563EB] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-[#6B7280]">{t.certifications.trustEyebrow}</div>
              <div className="font-display text-base font-semibold text-[#111111]">{t.certifications.trustTitle}</div>
            </div>
          </div>
          <div className="hidden md:block h-12 w-px bg-[#E5E7EB]" />
          <p className="text-sm text-[#6B7280] flex-1">
            {t.certifications.trustDesc}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Certifications;
